"use client";

import { notFound } from "next/navigation";
import Image from "next/image";
import { useLocale } from "@/context/LocaleContext";
import { getLocalizedFeatureDetails } from "@/data/landingPage/FeatureMenuData";

import AllInOneFeature from "@/features/landingPage/feature/AllInOneFeature";
import AutoShareFeature from "@/features/landingPage/feature/AutoShareFeature";
import QRCheckinFeature from "@/features/landingPage/feature/QRCheckinFeature";
import DigitalEnvelopeFeature from "@/features/landingPage/feature/DigitalEnvelopeFeature";
import CustomRSVPFeature from "@/features/landingPage/feature/CustomRSVPFeature";
import ReminderEventFeature from "@/features/landingPage/feature/ReminderEventFeature";
import MultiEventFeature from "@/features/landingPage/feature/MultiEventFeature";
import GiftRegistry from "@/features/landingPage/feature/GiftRegistry";

export default function FeatureDetailClient({ subfeature }: { subfeature: string }) {
  const { locale, t } = useLocale();

  const featuresData = getLocalizedFeatureDetails(locale);
  const content = featuresData[subfeature];

  if (!content) {
    notFound();
  }

  if (subfeature === "all-in-one-invitation") {
    return <AllInOneFeature />;
  }

  const renderBottomContent = () => {
    switch (subfeature) {
      case "whatsapp-broadcast": return <AutoShareFeature />;
      case "digital-envelope": return <DigitalEnvelopeFeature />;
      case "qr-check-in": return <QRCheckinFeature />;
      case "custom-rsvp": return <CustomRSVPFeature />;
      case "event-reminders": return <ReminderEventFeature />;
      case "multi-event-access": return <MultiEventFeature />;
      case "gift-registry": return <GiftRegistry />;
      default: return null;
    }
  };

  return (
    <main className="min-h-screen pt-30 md:pt-32 pb-10 bg-gray-50">
      <div className="px-4 md:px-8 max-w-[1200px] mx-auto mb-6">
        <h1 className="text-3xl md:text-[2.2rem] lg:text-[2.5rem] font-extrabold text-gray-900 tracking-tight">
          {content.title}
        </h1>
      </div>

      <section className="w-full bg-white border-y border-gray-100 mb-10">
        <div className="px-4 md:px-8 max-w-[1200px] mx-auto py-12 md:py-16">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8 lg:gap-12">

            <div className="w-full md:w-[75%] lg:w-[80%] space-y-5 relative z-20">
              <h2 className="text-2xl md:text-3xl lg:text-[2rem] font-bold text-gray-900 leading-tight">
                {content.subtitle}
              </h2>

              <div className="text-gray-700 text-[15px] md:text-lg leading-relaxed">
                {content.description
                  .split("\n")
                  .filter((line: string) => line.trim() !== "")
                  .map((line: string, index: number) => (
                    <p
                      key={index}
                      className={index > 0 ? "mt-4" : ""}
                      dangerouslySetInnerHTML={{ __html: line }}
                    />
                  ))}
              </div>
            </div>

            <div className="w-full md:w-[25%] lg:w-[20%] flex justify-end relative z-10">
              <div className="relative w-full max-w-[450px] aspect-[4/3] md:aspect-square lg:aspect-[4/3]">
                {content.imageUrl ? (
                  <Image
                    src={content.imageUrl}
                    alt={content.title}
                    fill
                    className={`object-contain object-right origin-right transition-transform duration-300 ${
                      subfeature === "digital-envelope"
                        ? "scale-100 md:scale-[1.2] scale-[0.8]"
                        : "scale-110 md:scale-[1.6] -translate-y-4 md:ml-24 md:-translate-y-8 md:mt-8 mt-8"
                    }`}
                  />
                ) : (
                  <div className="w-full h-full bg-rose-50/50 border-2 border-dashed border-rose-200 rounded-3xl flex items-center justify-center text-rose-400 font-medium">
                    {t("LandingPage.no_image") || "Gambar belum tersedia"}
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      </section>

      <section>
        {renderBottomContent()}
      </section>
    </main>
  );
}